'use client';

import { useAtomValue } from 'jotai';
import Link from 'next/link';
import { useState } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { css } from '@flipster/styled-system/css';
import { center } from '@flipster/styled-system/patterns';
import CheveronDown from '@flipster/ui/assets/svgs/chevron-down.svg';

import { filteredGlossaryTermsAtom } from '../jotai';
import { GlossaryPageGlossaryTermFragment } from '../query.generated';

// prettier-ignore
const indexItems = ['#', 'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z'];

const IndexNav = () => {
  const filteredGlossaryTerms = useAtomValue(filteredGlossaryTermsAtom);
  const termByIndex = filteredGlossaryTerms.reduce(
    (memo, term) => {
      let index = term.name?.toLowerCase().at(0);
      if (!index) return memo;
      if (/^[^a-zA-Z]$/.test(index)) index = '#';
      if (memo[index]) return memo;
      return { ...memo, [index]: term };
    },
    {} as Record<string, GlossaryPageGlossaryTermFragment>,
  );
  const [isOpen, setIsOpen] = useState(false);

  return (
    <nav className={css({ mb: 24, tablet: { mb: 50 } })} id='glossary-index-nav'>
      <h2 className={css({ display: 'none', tablet: { display: 'block', textStyle: 'Title/M', mb: 20 } })}>
        <FormattedMessage id='Autogen_common.r251126.s_Glossary_A_to_Z' />
      </h2>
      <ol
        className={css({
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(40px, 1fr))',
          gridAutoRows: 40,
          gap: 7,
          tabletDown: { h: '100%', overflow: 'hidden', maxH: !isOpen ? 40 : undefined },
          tablet: { gap: 6, gridTemplateColumns: 'repeat(auto-fill, minmax(38px, 1fr))' },
        })}
      >
        {indexItems.map((indexItem) => (
          <li
            className={css({
              tabletDown: indexItem === indexItems.at(-1) && !isOpen ? { gridRow: 1, gridColumn: -3 } : undefined,
            })}
            key={indexItem}
          >
            <Link
              className={css(liChildrenStyles, {
                color: 'currentcolor',
                textDecoration: 'none',
                outlineColor: 'transparent',
              })}
              href={termByIndex[indexItem] ? { hash: termByIndex[indexItem].slug } : '#'}
              role='button'
              tabIndex={-1}
              aria-disabled={!termByIndex[indexItem]}
            >
              {indexItem.toUpperCase()}
            </Link>
          </li>
        ))}

        <li
          className={css({
            tabletDown: { gridRow: 1, gridColumn: -4, display: isOpen ? 'none' : undefined },
            tablet: { display: 'none' },
          })}
        >
          <span className={css(liChildrenStyles, { pointerEvents: 'none', borderColor: 'transparent', opacity: 0.4 })}>
            ...
          </span>
        </li>

        <li
          className={css({
            tabletDown: {
              gridRow: 1,
              gridColumn: -2,
              '& svg': { transition: 'transform 0.25s ease-out', transform: isOpen ? 'rotate(-180deg)' : undefined },
            },
            tablet: { display: 'none' },
          })}
        >
          <button
            type='button'
            className={css(liChildrenStyles, { color: 'currentcolor' })}
            onClick={() => setIsOpen((prev) => !prev)}
            aria-hidden
            tabIndex={-1}
          >
            <CheveronDown />
          </button>
        </li>
      </ol>
    </nav>
  );
};

const liChildrenStyles = center.raw({
  textStyle: 'Legacy/H5',
  w: '100%',
  h: '100%',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'legacy.gray.4',
  rounded: 8,
  transition: 'opacity 0.3s ease-out, border-color 0.3s ease-out',
  '&:focus': {
    borderColor: 'legacy.blue',
  },
});

export default IndexNav;
